import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, RequireRole } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/common/Loader";
import { UpdateBanner } from "@/components/common/UpdateBanner";

const Landing = lazy(() => import("@/pages/public/Landing"));
const BusinessPage = lazy(() => import("@/pages/public/BusinessPage"));
const BookingPage = lazy(() => import("@/pages/public/BookingPage"));
const ManageBooking = lazy(() => import("@/pages/public/ManageBooking"));
const Privacy = lazy(() => import("@/pages/public/Privacy"));
const RouteFallback = lazy(() => import("@/pages/public/RouteFallback"));
const Login = lazy(() => import("@/pages/auth/Login"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Agenda = lazy(() => import("@/pages/admin/Agenda"));
const Barbers = lazy(() => import("@/pages/admin/Barbers"));
const Services = lazy(() => import("@/pages/admin/Services"));
const Customers = lazy(() => import("@/pages/admin/Customers"));
const Pets = lazy(() => import("@/pages/admin/Pets"));
const Schedules = lazy(() => import("@/pages/admin/Schedules"));
const Blocks = lazy(() => import("@/pages/admin/Blocks"));
const Waitlist = lazy(() => import("@/pages/admin/Waitlist"));
const Reports = lazy(() => import("@/pages/admin/Reports"));
const PublicPageEditor = lazy(() => import("@/pages/admin/PublicPageEditor"));
const PublicPreview = lazy(() => import("@/pages/admin/PublicPreview"));
const Share = lazy(() => import("@/pages/admin/Share"));
const Categories = lazy(() => import("@/pages/admin/Categories"));
const Subscription = lazy(() => import("@/pages/admin/Subscription"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const BarberLayout = lazy(() => import("@/layouts/BarberLayout"));
const BarberHome = lazy(() => import("@/pages/barber/BarberHome"));
const BarberClients = lazy(() => import("@/pages/barber/BarberClients"));
const BarberProfile = lazy(() => import("@/pages/barber/BarberProfile"));
const BarberDashboard = lazy(() => import("@/pages/barber/BarberDashboard"));
const BarberSchedule = lazy(() => import("@/pages/barber/BarberSchedule"));
const SuperLayout = lazy(() => import("@/layouts/SuperLayout"));
const SuperDashboard = lazy(() => import("@/pages/super/SuperDashboard"));
const SuperBusinesses = lazy(() => import("@/pages/super/Businesses"));
const SuperModules = lazy(() => import("@/pages/super/Modules"));
const SuperUsers = lazy(() => import("@/pages/super/Users"));
const SuperReports = lazy(() => import("@/pages/super/Reports"));
const SuperUpgrades = lazy(() => import("@/pages/super/UpgradeRequests"));
const SuperBusinessDetail = lazy(() => import("@/pages/super/BusinessDetail"));
const SuperPlans = lazy(() => import("@/pages/super/Plans"));
const SuperSubscriptions = lazy(() => import("@/pages/super/Subscriptions"));
const SuperPayments = lazy(() => import("@/pages/super/Payments"));
const SuperRevenue = lazy(() => import("@/pages/super/Revenue"));
const SuperNotices = lazy(() => import("@/pages/super/Notices"));
const SuperSettings = lazy(() => import("@/pages/super/PlatformSettings"));
const SuperBackups = lazy(() => import("@/pages/super/Backups"));

function LegacyBookingRedirect() {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/b/${slug}/agendar${search}`} replace />;
}

import { routerBasename } from "@/lib/tenantPath";

export default function App() {
  return (
    <BrowserRouter basename={routerBasename()}>
      <AuthProvider>
        <UpdateBanner />
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/barbeiro/login" element={<Login />} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/admin/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/barbeiro/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/privacidade" element={<Privacy />} />
            <Route path="/b/:slug" element={<BusinessPage />} />
            <Route path="/b/:slug/agendar" element={<BookingPage />} />
            <Route path="/b/:slug/marcacao/:token" element={<ManageBooking />} />
            <Route path="/agendar/:slug" element={<LegacyBookingRedirect />} />
            <Route path="/admin" element={<RequireRole roles={["business_admin", "super_admin"]}><AdminLayout /></RequireRole>}>
              <Route index element={<Dashboard />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="agendamentos" element={<Navigate to="/admin/agenda?tab=lista" replace />} />
              <Route path="barbeiros" element={<Barbers />} />
              <Route path="servicos" element={<Services />} />
              <Route path="clientes" element={<Customers />} />
              <Route path="pets" element={<Pets />} />
              <Route path="horarios" element={<Schedules />} />
              <Route path="bloqueios" element={<Blocks />} />
              <Route path="lista-espera" element={<Waitlist />} />
              <Route path="relatorios" element={<Reports />} />
              <Route path="pagina" element={<PublicPageEditor />} />
              <Route path="ver-pagina" element={<PublicPreview />} />
              <Route path="partilhar" element={<Share />} />
              <Route path="categorias" element={<Categories />} />
              <Route path="assinatura" element={<Subscription />} />
              <Route path="configuracoes" element={<Settings />} />
            </Route>
            <Route path="/barbeiro" element={<RequireRole roles={["barber"]}><BarberLayout /></RequireRole>}>
              <Route index element={<BarberHome />} />
              <Route path="painel" element={<BarberDashboard />} />
              <Route path="horarios" element={<BarberSchedule />} />
              <Route path="clientes" element={<BarberClients />} />
              <Route path="perfil" element={<BarberProfile />} />
            </Route>
            <Route path="/super-admin" element={<RequireRole roles={["super_admin"]}><SuperLayout /></RequireRole>}>
              <Route index element={<SuperDashboard />} />
              <Route path="empresas" element={<SuperBusinesses />} />
              <Route path="empresas/:id" element={<SuperBusinessDetail />} />
              <Route path="modulos" element={<SuperModules />} />
              <Route path="utilizadores" element={<SuperUsers />} />
              <Route path="relatorios" element={<SuperReports />} />
              <Route path="barbearias" element={<Navigate to="/super-admin/empresas" replace />} />
              <Route path="barbearias/:id" element={<SuperBusinessDetail />} />
              <Route path="planos" element={<SuperPlans />} />
              <Route path="assinaturas" element={<SuperSubscriptions />} />
              <Route path="pagamentos" element={<SuperPayments />} />
              <Route path="receita" element={<SuperRevenue />} />
              <Route path="avisos" element={<SuperNotices />} />
              <Route path="pedidos" element={<SuperUpgrades />} />
              <Route path="configuracoes" element={<SuperSettings />} />
              <Route path="backups" element={<SuperBackups />} />
            </Route>
            <Route path="/super/*" element={<Navigate to="/super-admin" replace />} />
            <Route path="*" element={<RouteFallback />} />
          </Routes>
        </Suspense>
        <Toaster theme="dark" position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
